import React, { useEffect, useState } from "react";
import { ColumnConfig } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { UHCJobStatisticsResult } from "@app/operations/jobStatisticsResult";
import OperationsService from "@app/operations/operationsService";
import { getMostRecentWednesday } from "@app/shared/utility/getMostRecentDay";

interface UHCJobStatisticsProps {
  clients: { clientId: number; name: string }[];
  clientId: number | null;
  setClientId: React.Dispatch<React.SetStateAction<number | null>>;
}

const operationsService = new OperationsService();
const ALL_LINES_OF_BUSINESS = "all";
const OTHER_LINES_OF_BUSINESS = "other";
const LOB_SELECT_ERROR_TEXT = "Please select a Line of Business";
const ALL_JOB_TYPES = 0;
const JOB_TYPES = [
  { label: "1001 - Custom Adequacy", value: 1001 },
  { label: "1002 - Custom Adequacy Region Summary", value: 1002 },
];

const humanaColumns: ColumnConfig = {
  avgRunTimeSeconds: {
    displayText: "Average Runtime Seconds",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__average-runtime-seconds-output-humana",
  },
  jobType: {
    displayText: "Job Type",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__job-type-output-humana",
  },
  lineOfBusiness: {
    displayText: "LOB",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__lob-output-humana",
  },
  percentComplete: {
    displayText: "Percent Complete",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__percent-complete-output-humana",
  },
  runDate: {
    displayText: "Run Date",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__run-date-output-humana",
  },
  successfulJobCount: {
    displayText: "Successful Job Count",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__successful-job-count-output-humana",
  },
  totalJobCount: {
    displayText: "Total Job Count",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__total-job-count-output-humana",
  },
  userName: {
    displayText: "User Name",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__user-name-output-humana",
  },
};

const UHCJobStatistics: React.FC<UHCJobStatisticsProps> = ({ clients, clientId, setClientId }) => {
  const [uhcJobStatistics, setUHCJobStatistics] = useState<UHCJobStatisticsResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [lineOfBusiness, setLineOfBusiness] = useState<string | null>(ALL_LINES_OF_BUSINESS);
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState<string[]>([]);
  const [weekStartDate, setWeekStartDate] = useState<string>(getMostRecentWednesday());
  const [searchUsernameString, setSearchUsernameString] = useState<string>('');
  const [jobType, setJobType] = useState<number>(ALL_JOB_TYPES);

  useEffect(() => {
    if (clientId != null) {
      calculateHumanaJobStatistics();
    }
  }, []);

  useEffect(() => {
    resetFormValues();
    if (clientId == null) {
      return;
    }

    operationsService
      .getLinesOfBusiness(clientId, true)
      .then(
        linesOfBusiness => {
          setLineOfBusinessOptions(linesOfBusiness);
          if (linesOfBusiness.length === 0) setError("No Line of Business options available for this client.");
        },
        () => setError("Error fetching Line of Business options")
      );
  }, [clientId]);

  const resetFormValues = () => {
    setError('');
    setLineOfBusinessOptions([]);
    setLineOfBusiness(ALL_LINES_OF_BUSINESS);
    setUHCJobStatistics([]);
  };

  const calculateHumanaJobStatistics = async () => {
    if (!clientId) {
      setError('Please select a Client');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const adjustedLineOfBusiness = lineOfBusiness === ALL_LINES_OF_BUSINESS ? "" : lineOfBusiness;
      const response = await operationsService.getUHCJobStatistics(weekStartDate, adjustedLineOfBusiness, clientId);
      const filteredJobType = jobType !== 0 ? response.filter(item => (item.jobType === jobType || jobType === item.jobType)) : response;
      const filteredUser = searchUsernameString ? filteredJobType.filter(item => (item.userName.includes(searchUsernameString))) : filteredJobType;
      setUHCJobStatistics(filteredUser);
    } catch (error) {
      setError('Error calculating job statistics');
    } finally {
      setLoading(false);
    }
  };

  const isLOBSelectionInvalid = lineOfBusiness === null;

  return (
    <div>
      <div className="form-row mb-3">
        <div className="col pe-1">
          <QESSelect
            id="clientSelect"
            value={clientId !== null ? clientId.toString() : ''}
            onChange={(event) => {
              const selectedClientId = Number(event.target.value);
              setClientId(selectedClientId);
            }}
            placeholder="Select Client"
            testId="weekend-run-monitoring__client-select-humana"
            label="Client"
            required
            isValid
          >
            <option value="">Select Client</option>
            {clients.map((client) => (
              <option key={client.clientId} value={client.clientId.toString()}>
                {client.name}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col px-1">
          <QESSelect
            id="lineOfBusinessSelect"
            value={lineOfBusiness ?? ""}
            onChange={(event) => setLineOfBusiness(event.target.value)}
            placeholder="Select a Line Of Business"
            testId="weekend-run-monitoring__line-of-business-select-uhc"
            label="Line of Business"
            disabled={lineOfBusinessOptions.length === 0}
            isValid={lineOfBusinessOptions.length === 0 || !isLOBSelectionInvalid}
            errorText={LOB_SELECT_ERROR_TEXT}
            required
            positionDescriptionTextAbsolutely={true}
          >
            {lineOfBusiness.length > 0 && (<option value={ALL_LINES_OF_BUSINESS}>All</option>)}
            {lineOfBusinessOptions.map((lob) => (
              <option key={lob.toLowerCase()} value={lob.toLowerCase()}>
                {lob.toUpperCase()}
              </option>
            ))}
            {lineOfBusiness.length > 0 && (<option value={OTHER_LINES_OF_BUSINESS}>Other</option>)}
          </QESSelect>
        </div>
        <div className="col px-1">
          <QESSelect
            label="Job Type Filter"
            value={jobType}
            onChange={e => setJobType(Number(e.target.value))}
            testId="weekend-run-monitoring__job-type-select"
            disabled={clientId === null}
          >
            <option value={ALL_JOB_TYPES}>All</option>
            {JOB_TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col px-1">
          <QESDateInput
            label="Week Start Date"
            value={weekStartDate}
            onChange={(event) => setWeekStartDate(event.target.value)}
            testId="weekend-run-monitoring__week-start-input-humana"
          />
        </div>
        <div className="col px-1">
          <QESTextInput label="Username Filter" testId="weekend-run-monitoring-filter__username" value={searchUsernameString} onChange={(event) => setSearchUsernameString(event.target.value)} />
        </div>
        <div className="col d-flex align-items-end ps-1">
          <QESButton onClick={calculateHumanaJobStatistics} qesType="primary" qesTestId="weekend-run-monitoring__calculate-job-statistics-humana">
            Search
          </QESButton>
        </div>
      </div>
      <div className="mt-4">
        {loading && <div>Loading...</div>}
        {error && <div>{error}</div>}
        {uhcJobStatistics.length > 0 && (
          <Waffles
            columns={humanaColumns}
            data={uhcJobStatistics.map((stat, index) => ({
              /* oxlint-disable sort-keys */
              key: index.toString(),
              userName: stat.userName,
              runDate: new Date(stat.runDate).toLocaleDateString(),
              lineOfBusiness: stat.lineOfBusiness,
              jobType: stat.jobType,
              totalJobCount: stat.totalJobCount,
              successfulJobCount: stat.successfulJobCount,
              avgRunTimeSeconds: stat.avgRunTimeSeconds,
              percentComplete: stat.percentComplete,
              /* oxlint-enable sort-keys */
            }))}
            enablePagination={false}
            passThroughSorting={true}
            testId="weekend-run-monitoring__job-statistics-table-humana"
          />
        )}
      </div>
    </div>
  );
};

export default UHCJobStatistics;